import { useCallback, useEffect, useState, useRef } from 'react';
import { MemeCanvas } from './MemeCanvas';
import { MemeSidebar } from './MemeSidebar';
import { MemeNFTSearch } from './MemeNFTSearch';
import { UndoButton } from './UndoButton';
import { BannerCanvas, BannerToolbar, BannerSidebar, BannerNFTSearch } from '../banner';
import { Button, ImagePreviewModal } from '../common';
import { useMemeStore, useBannerStore, useNFTStore } from '../../stores';
import { downloadImage, drawWrappedText, isBackgroundLayer, isCharacterLayer, isFrameLayer } from '../../utils';
import { playClickSound } from '../../utils/gameSound';

type BuilderTab = 'memes' | 'banner';

interface MemeBuilderProps {
  onClose?: () => void;
}

export function MemeBuilder({ onClose }: MemeBuilderProps) {
  const [activeTab, setActiveTab] = useState<BuilderTab>('memes');
  const sidebarRef = useRef<HTMLDivElement>(null);

  const handleCharacterTap = useCallback(() => {
    sidebarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const { currentNFT } = useNFTStore();

  const { canvasWidth, canvasHeight, backgroundImage, backgroundImageScale, backgroundImageOffset, backgroundColor, nftElements, textElements } =
    useMemeStore();

  const {
    canvasWidth: bannerWidth,
    canvasHeight: bannerHeight,
    backgroundImage: bannerBgImage,
    backgroundColor: bannerBgColor,
    elements: bannerElements,
    addNFTElement: addToBanner
  } = useBannerStore();

  // Undo keyboard shortcut (Ctrl/Cmd+Z)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (activeTab === 'memes') {
          useMemeStore.getState().undo();
        } else {
          useBannerStore.getState().undo();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTab]);

  // Preview modal state for Telegram fallback
  const [previewImage, setPreviewImage] = useState<{ url: string; filename: string } | null>(null);

  const handleTabChange = (tab: BuilderTab) => {
    if (tab !== activeTab) {
      playClickSound();

      // Auto-add NFT to banner canvas when switching to banner tab
      if (tab === 'banner' && currentNFT) {
        const alreadyInBanner = bannerElements.some(el => el.nftData.mintAddress === currentNFT.mintAddress);
        if (!alreadyInBanner) {
          addToBanner(currentNFT);
        }
      }

      setActiveTab(tab);
    }
  };

  const handleExport = useCallback(async () => {
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = canvasWidth;
    exportCanvas.height = canvasHeight;

    const ctx = exportCanvas.getContext('2d');
    if (!ctx) {
      console.error('Could not get canvas context');
      return;
    }

    const loadImage = (url: string): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });
    };

    // Draw background color first
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Draw uploaded background image (scaled, centered, and panned — mirrors MemeCanvas)
    if (backgroundImage) {
      try {
        const bgImg = await loadImage(backgroundImage);
        const scaledWidth = bgImg.width * backgroundImageScale;
        const scaledHeight = bgImg.height * backgroundImageScale;
        const drawX = (canvasWidth - scaledWidth) / 2 + backgroundImageOffset.x;
        const drawY = (canvasHeight - scaledHeight) / 2 + backgroundImageOffset.y;

        ctx.drawImage(bgImg, drawX, drawY, scaledWidth, scaledHeight);
      } catch (err) {
        console.error('Failed to load background image:', err);
      }
    }

    // Sort NFT elements by zIndex for proper layering
    const sortedNFTElements = [...nftElements].sort((a, b) => a.zIndex - b.zIndex);

    // In sticker mode the canvas preview shows ONLY the sticker element (no text,
    // no other characters) — the export must match the preview.
    const stickerModeElement = nftElements.find((el) => el.stickerMode);

    // Combine and sort all elements by zIndex for character/text rendering
    const allElements: Array<{ type: 'nft' | 'text'; element: typeof nftElements[0] | typeof textElements[0] }> = stickerModeElement
      ? [{ type: 'nft' as const, element: stickerModeElement }]
      : [
          ...nftElements.map((e) => ({ type: 'nft' as const, element: e })),
          ...textElements.map((e) => ({ type: 'text' as const, element: e })),
        ].sort((a, b) => a.element.zIndex - b.element.zIndex);

    // Helper function to draw background layer to fill canvas
    const drawBackgroundLayer = (layerImg: HTMLImageElement) => {
      const imgAspect = layerImg.width / layerImg.height;
      const canvasAspect = canvasWidth / canvasHeight;

      let drawWidth: number;
      let drawHeight: number;
      let drawX: number;
      let drawY: number;

      if (imgAspect > canvasAspect) {
        drawHeight = canvasHeight;
        drawWidth = drawHeight * imgAspect;
        drawX = (canvasWidth - drawWidth) / 2;
        drawY = 0;
      } else {
        drawWidth = canvasWidth;
        drawHeight = drawWidth / imgAspect;
        drawX = 0;
        drawY = (canvasHeight - drawHeight) / 2;
      }

      ctx.drawImage(layerImg, drawX, drawY, drawWidth, drawHeight);
    };

    const shouldRenderNFTBackgrounds = !backgroundImage;

    // PASS 1: Draw ALL background layers first (in z-order)
    // Skipped entirely in sticker mode (mirrors MemeCanvas)
    if (shouldRenderNFTBackgrounds && !stickerModeElement) {
      for (const nftEl of sortedNFTElements) {
        if (!nftEl.useBackgroundLayer) continue;

        const backgroundLayers = nftEl.nftData.layerAssets.filter(isBackgroundLayer);

        for (const layer of backgroundLayers) {
          try {
            const layerImg = await loadImage(layer.uri);
            drawBackgroundLayer(layerImg);
          } catch (err) {
            console.error('Failed to load background layer for export:', layer.id, err);
          }
        }
      }
    }

    // PASS 2: Draw ALL character layers and text elements (in z-order)
    for (const { type, element } of allElements) {
      ctx.save();

      if (type === 'nft') {
        const nftEl = element as typeof nftElements[0];
        const layers = nftEl.nftData.layerAssets;

        ctx.translate(nftEl.position.x, nftEl.position.y);
        ctx.rotate((nftEl.rotation * Math.PI) / 180);

        if (layers.length > 0) {
          const characterLayers = layers.filter((layer) => {
            if (nftEl.selectedLayers.length > 0) {
              return nftEl.selectedLayers.includes(layer.id);
            }

            // Background/frame handled in pass 1 / pass 3
            return isCharacterLayer(layer);
          });

          // Get reference dimensions from composite NFT image for consistent layer scaling
          let refWidth = 512;
          let refHeight = 512;
          try {
            const refImg = await loadImage(nftEl.nftData.imageUrl);
            refWidth = refImg.width;
            refHeight = refImg.height;
          } catch {
            // Use default dimensions if composite image fails to load
          }

          for (const layer of characterLayers) {
            try {
              const layerImg = await loadImage(layer.uri);
              const scaledWidth = refWidth * nftEl.scale;
              const scaledHeight = refHeight * nftEl.scale;
              ctx.drawImage(
                layerImg,
                -scaledWidth / 2,
                -scaledHeight / 2,
                scaledWidth,
                scaledHeight
              );
            } catch (err) {
              console.error('Failed to load layer for export:', layer.id, err);
            }
          }
        } else {
          // Fallback: draw full NFT image at position and scale
          try {
            const img = await loadImage(nftEl.nftData.imageUrl);
            const scaledWidth = img.width * nftEl.scale;
            const scaledHeight = img.height * nftEl.scale;
            ctx.drawImage(img, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
          } catch (err) {
            console.error('Failed to load NFT image for export:', err);
          }
        }
      } else {
        const textEl = element as typeof textElements[0];
        ctx.translate(textEl.position.x, textEl.position.y);
        ctx.rotate((textEl.rotation * Math.PI) / 180);

        ctx.direction = 'ltr';
        ctx.font = `${textEl.fontSize}px ${textEl.fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        drawWrappedText(ctx, textEl.text, textEl.fontSize, canvasWidth * 0.9, {
          color: textEl.color,
          strokeColor: textEl.strokeColor,
          strokeWidth: textEl.strokeWidth,
        });
      }

      ctx.restore();
    }

    // PASS 3: Draw ALL frame layers last (in z-order)
    // Skipped entirely in sticker mode (mirrors MemeCanvas)
    if (!stickerModeElement) {
    for (const nftEl of sortedNFTElements) {
      if (!nftEl.useFrameLayer) continue;

      const frameLayers = nftEl.nftData.layerAssets.filter(isFrameLayer);

      for (const layer of frameLayers) {
        try {
          const layerImg = await loadImage(layer.uri);
          drawBackgroundLayer(layerImg);
        } catch (err) {
          console.error('Failed to load frame layer for export:', layer.id, err);
        }
      }
    }
    } // end if (!stickerModeElement)

    // Convert to PNG blob and download using smart download utility
    exportCanvas.toBlob(async (blob) => {
      if (blob) {
        const filename = `nft_meme_${canvasWidth}x${canvasHeight}.png`;

        await downloadImage(blob, filename, {
          onPreviewRequired: (dataUrl, fname) => {
            setPreviewImage({ url: dataUrl, filename: fname });
          },
        });
      }
    }, 'image/png');
  }, [canvasWidth, canvasHeight, backgroundImage, backgroundImageScale, backgroundImageOffset, backgroundColor, nftElements, textElements]);

  // Banner export handler
  const handleBannerExport = useCallback(async () => {
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = bannerWidth;
    exportCanvas.height = bannerHeight;

    const ctx = exportCanvas.getContext('2d');
    if (!ctx) {
      console.error('Could not get canvas context');
      return;
    }

    const loadImage = (url: string): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });
    };

    if (bannerBgImage) {
      try {
        const bgImg = await loadImage(bannerBgImage);
        const imgAspect = bgImg.width / bgImg.height;
        const canvasAspect = bannerWidth / bannerHeight;

        let drawWidth: number;
        let drawHeight: number;
        let drawX: number;
        let drawY: number;

        if (imgAspect > canvasAspect) {
          drawHeight = bannerHeight;
          drawWidth = drawHeight * imgAspect;
          drawX = (bannerWidth - drawWidth) / 2;
          drawY = 0;
        } else {
          drawWidth = bannerWidth;
          drawHeight = drawWidth / imgAspect;
          drawX = 0;
          drawY = (bannerHeight - drawHeight) / 2;
        }

        ctx.drawImage(bgImg, drawX, drawY, drawWidth, drawHeight);
      } catch (err) {
        console.error('Failed to load background image:', err);
        ctx.fillStyle = bannerBgColor;
        ctx.fillRect(0, 0, bannerWidth, bannerHeight);
      }
    } else {
      ctx.fillStyle = bannerBgColor;
      ctx.fillRect(0, 0, bannerWidth, bannerHeight);
    }

    const sortedElements = [...bannerElements].sort((a, b) => a.zIndex - b.zIndex);

    const drawFullCanvasLayer = (layerImg: HTMLImageElement, verticalOffset: number = 0) => {
      const imgAspect = layerImg.width / layerImg.height;
      const canvasAspect = bannerWidth / bannerHeight;

      let drawWidth: number;
      let drawHeight: number;
      let drawX: number;

      if (imgAspect > canvasAspect) {
        drawHeight = bannerHeight;
        drawWidth = drawHeight * imgAspect;
        drawX = (bannerWidth - drawWidth) / 2;
      } else {
        drawWidth = bannerWidth;
        drawHeight = drawWidth / imgAspect;
        drawX = 0;
      }

      const verticalOverflow = drawHeight - bannerHeight;
      const yOffset = (verticalOffset / 100) * (verticalOverflow / 2);
      const drawY = (bannerHeight - drawHeight) / 2 + yOffset;

      ctx.drawImage(layerImg, drawX, drawY, drawWidth, drawHeight);
    };

    const shouldRenderNFTBackgrounds = !bannerBgImage;

    // PASS 1: Draw ALL background layers first
    if (shouldRenderNFTBackgrounds) {
      for (const element of sortedElements) {
        if (!element.useBackgroundLayer) continue;
        const backgroundLayers = element.nftData.layerAssets.filter(isBackgroundLayer);
        for (const layer of backgroundLayers) {
          try {
            const layerImg = await loadImage(layer.uri);
            drawFullCanvasLayer(layerImg, element.backgroundVerticalOffset || 0);
          } catch (err) {
            console.error('Failed to load background layer:', err);
          }
        }
      }
    }

    // PASS 2: Draw ALL character layers
    for (const element of sortedElements) {
      ctx.save();
      const layers = element.nftData.layerAssets;
      ctx.translate(element.position.x, element.position.y);
      ctx.rotate((element.rotation * Math.PI) / 180);

      if (layers.length > 0) {
        const characterLayers = layers.filter((layer) => {
          if (element.selectedLayers.length > 0) {
            return element.selectedLayers.includes(layer.id);
          }
          return isCharacterLayer(layer);
        });

        let refWidth = 512;
        let refHeight = 512;
        try {
          const refImg = await loadImage(element.nftData.imageUrl);
          refWidth = refImg.width;
          refHeight = refImg.height;
        } catch {
          // Use default dimensions
        }

        for (const layer of characterLayers) {
          try {
            const layerImg = await loadImage(layer.uri);
            const scaledWidth = refWidth * element.scale;
            const scaledHeight = refHeight * element.scale;
            ctx.drawImage(layerImg, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
          } catch (err) {
            console.error('Failed to load layer:', err);
          }
        }
      } else {
        try {
          const img = await loadImage(element.nftData.imageUrl);
          const scaledWidth = img.width * element.scale;
          const scaledHeight = img.height * element.scale;
          ctx.drawImage(img, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
        } catch (err) {
          console.error('Failed to load NFT image:', err);
        }
      }
      ctx.restore();
    }

    // PASS 3: Draw ALL frame layers last
    for (const element of sortedElements) {
      if (!element.useFrameLayer) continue;
      const frameLayers = element.nftData.layerAssets.filter(isFrameLayer);
      for (const layer of frameLayers) {
        try {
          const layerImg = await loadImage(layer.uri);
          drawFullCanvasLayer(layerImg, element.frameVerticalOffset || 0);
        } catch (err) {
          console.error('Failed to load frame layer:', err);
        }
      }
    }

    exportCanvas.toBlob(async (blob) => {
      if (blob) {
        const filename = `nft_banner_${bannerWidth}x${bannerHeight}.png`;
        await downloadImage(blob, filename, {
          onPreviewRequired: (dataUrl, fname) => {
            setPreviewImage({ url: dataUrl, filename: fname });
          },
        });
      }
    }, 'image/png');
  }, [bannerWidth, bannerHeight, bannerBgImage, bannerBgColor, bannerElements]);

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      {/* Tab Header */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 p-1 bg-surface-secondary dark:bg-neutral-800/50 rounded-xl">
            <button
              onClick={() => handleTabChange('memes')}
              className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                activeTab === 'memes'
                  ? 'bg-game-blue text-white shadow-md'
                  : 'text-text-muted dark:text-neutral-400 hover:text-text-dark dark:hover:text-white hover:bg-surface dark:hover:bg-neutral-700/50'
              }`}
            >
              Memes
            </button>
            <button
              onClick={() => handleTabChange('banner')}
              className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                activeTab === 'banner'
                  ? 'bg-game-blue text-white shadow-md'
                  : 'text-text-muted dark:text-neutral-400 hover:text-text-dark dark:hover:text-white hover:bg-surface dark:hover:bg-neutral-700/50'
              }`}
            >
              Banner
            </button>
          </div>
          <UndoButton activeTab={activeTab} />
        </div>
        {onClose && (
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        )}
      </div>

      {/* Memes Tab Content */}
      {activeTab === 'memes' && (
        <>
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-5 flex-1 min-h-0 overflow-hidden">
            {/* Canvas area - stays visible on mobile */}
            <div className="w-full lg:flex-1 min-w-0">
              <div className="sticky top-0 z-10 pb-3">
                <div className="w-full flex justify-center">
                  <MemeCanvas onCharacterTap={handleCharacterTap} />
                </div>
                <div className="mt-4 flex justify-center">
                  <Button variant="primary" onClick={handleExport} className="px-8">
                    Export PNG
                  </Button>
                </div>
              </div>
            </div>

            {/* Sidebar - only this area scrolls on mobile */}
            <div
              ref={sidebarRef}
              className="w-full lg:w-80 flex-1 lg:flex-shrink-0 min-h-0 overflow-hidden overscroll-contain"
            >
              <MemeSidebar />
            </div>
          </div>
          <MemeNFTSearch />
        </>
      )}

      {/* Banner Tab Content */}
      {activeTab === 'banner' && (
        <>
          <div className="flex flex-col lg:flex-row gap-5 flex-1 min-h-0">
            {/* Left side - toolbar + canvas + export */}
            <div className="flex-1 flex flex-col min-w-0 overflow-y-auto">
              <BannerToolbar />
              <div className="flex-1 flex flex-col items-center justify-start">
                <div className="w-full max-w-[900px] px-2 sm:px-0">
                  <BannerCanvas maxViewportWidth={900} maxViewportHeight={350} />
                </div>
                <div className="mt-5 flex-shrink-0">
                  <Button variant="primary" onClick={handleBannerExport} className="px-8">
                    Export PNG
                  </Button>
                </div>
              </div>
            </div>
            {/* Sidebar - beside canvas on desktop, below on mobile */}
            <div className="w-full lg:w-80 flex-shrink-0 lg:overflow-y-auto">
              <BannerSidebar />
            </div>
          </div>
          <BannerNFTSearch />
        </>
      )}

      {/* Image Preview Modal for Telegram fallback */}
      <ImagePreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={previewImage?.url || ''}
        filename={previewImage?.filename || ''}
      />
    </div>
  );
}
